import React, {useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import {CustomSearch, PublicCustomSearch, SERVICE_ADVANCED_SEARCH} from '../reduxToolkit/Commons/CommonsApi';
import {DEBOUNCE_DELAY} from '../Apis/functions';
import AutoCompleteSearch from "./AutoCompleteSearch";
import {debounce} from "lodash";
import {SearchIcon} from "./buttons/IconButtons";

/**
 * CustomAutocompleteSearch Component
 *
 * Provides an advanced autocomplete input field that performs asynchronous searches
 * against a given API with debounce functionality. It allows filtering, pagination,
 * and custom rendering options for search results.
 *
 * @author Arnulfo Ferro
 * @createdAt 2025-10-21
 * @jobTitle Full Stack Developer - IBL
 * @email azapata@ibl.mx
 *
 * @param {string} [id='slt_cas_search'] - The ID of the autocomplete input element.
 * @param {string} [label='search'] - The label displayed above the input field.
 * @param {Object} itemSelected - The currently selected item in the autocomplete.
 * @param {Function} setItemSelected - Function to update the selected item state.
 * @param {Array} [userTypeIdsSearch=[]] - List of user type IDs to include in the search filter.
 * @param {boolean} [required=false] - Determines if the field is mandatory.
 * @param {boolean} [disabled=false] - Whether the autocomplete is disabled.
 * @param {string} [api=SERVICE_ADVANCED_SEARCH] - The API endpoint used for the search request.
 * @param {Object|null} [bodyApi=null] - Optional custom request body for the API.
 * @param {number} [rowsPerPage=20] - Number of rows per page in the API pagination.
 * @param {string} [model=TABLES.USER_BY_USER_TYPE] - The database model or table name for the search.
 * @param {string} [helperText=""] - Helper text displayed below the input field.
 * @param {boolean} [emptyFields] - Flag to indicate empty input validation.
 * @param {string} [key='id'] - Unique key used to map search results.
 * @param reset - The reset prop is used to determine whether the autocomplete input should clear its value when the selected item is reset to null.
 * @param {Function} [onReset=() => {}] - Callback function triggered when the input is reset.
 * @param Icon: Icon component (optional)
 * @param {Function} [filter] - Optional function to filter or transform the API response
 * before it is passed to `listTransform`. Receives the list of results and must
 * return the processed list.
 * @returns {JSX.Element} Rendered autocomplete search component with dynamic API-based suggestions.
 */

const CustomAutocompleteSearch = ({
                                      id = 'slt_cas_search',
                                      label = 'search',
                                      itemSelected,
                                      setItemSelected,
                                      userTypeIdsSearch = [],
                                      required = false,
                                      showRenderOption = true,
                                      disabled = false,
                                      api = SERVICE_ADVANCED_SEARCH,
                                      bodyApi = null,
                                      rowsPerPage = 20,
                                      model = null,
                                      helperText = "",
                                      emptyFields,
                                      keyId = "id",
                                      reset,
                                      onReset = () => {},
                                      handleSelected = () => {},
                                      listTransform,
                                      Icon,
                                      filter,
                                      cleanAutocompleteSearch,
                                      setCleanAutocompleteSearch
                                  }) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const dispatch = useDispatch();
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const isTyping = useRef(true);

    const handleSearch = (search) => {
        if (search.trim()) {
            handleApiSearch(search, userTypeIdsSearch);
        } else {
            setOptions([]);
            setOpen(false);
        }
    };

    const handleApiSearch = (search, userTypeIds) => {
        let body;
        if (bodyApi === null) {
            body = {
                key: search,
                pagination: { page: 0, size: rowsPerPage },
                userTypeIds,
                model
            };
        } else {
            body = { ...bodyApi, key: search };
        }

        const searchAction = api.includes("public/")
            ? PublicCustomSearch
            : CustomSearch;

        dispatch(searchAction({ body, api }))
            .then(({ payload: { data: { data } } }) => {
                let content = data?.content ??
                    (Array.isArray(data) && data.length > 0
                        ? data
                        : []);

                if(filter) content = filter(content);
                setOptions(listTransform(content, keyId, showRenderOption, Icon));
                setOpen(true);
            });
    };

    useEffect(() => {
        if (!isTyping.current) return;
        const debouncedFilter = debounce(() => handleSearch(inputValue), DEBOUNCE_DELAY);
        debouncedFilter();
        return () => debouncedFilter.cancel();
    }, [inputValue]);

    useEffect(() => {
        if (cleanAutocompleteSearch) {
            setItemSelected(null);
            setInputValue("");
            // reset flag in parent (if function)
            try { setCleanAutocompleteSearch(false); } catch (err) { /* no-op */ }
        }
    }, [cleanAutocompleteSearch, setCleanAutocompleteSearch]);

    return (
        <div className='flex-grow-1'>
            <AutoCompleteSearch
                id={id}
                helperText={helperText}
                data={options}
                value={itemSelected?.label || (reset && !itemSelected ? "" : inputValue) || ''}
                setValue={(e) => {
                    isTyping.current = false;
                    if (e === null) {
                        onReset();
                        if(reset) setInputValue("");
                    }
                    setItemSelected(e);
                    setTimeout(() => { isTyping.current = true }, 500);
                }}
                label={t(label)}
                required={required}
                emptyFields={emptyFields}
                disabled={disabled}
                onInputChange={(event, newInputValue) => {
                    isTyping.current = true;
                    setInputValue(newInputValue);
                }}
                handleFn={(e) => {
                    setItemSelected(e); //Update values in selected option or reset option
                    if(handleSelected) handleSelected(e); //Update values only selected option
                }}
                reset={reset}
                onKeyDownEnter={() => handleSearch(inputValue)}
                noOptionsCard={false}
                showRenderOption={showRenderOption}
                inputValue={inputValue}
                searchIcon={
                    <SearchIcon
                        width={16}
                        height={16}
                        className="icon-color-shades-500 ibl-font-w-600"
                        onClick={() => handleSearch(inputValue)}
                    />
                }
                open={open}
                setOpen={setOpen}
                onClose={() => setOpen(false)}
            />
        </div>
    );
};

export default CustomAutocompleteSearch
